import React from "react";
import { Box, Button, Checkbox, FormControlLabel, IconButton, Modal, Paper, TextField, Typography } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { useTheme } from "@mui/styles";
import CloseIcon from "@mui/icons-material/Close";
import SquarehubLoader from "bundles/common/components/SquarehubLoader";

const CloseJobApplicationModal = ({
  open,
  onClose,
  onConfirm,
  loading = false,
  candidateName = "",
  jobTitle = "",
  denialReasons = [],
  otherReason = "",
  onToggleReason,
  onChangeOtherReason,
}) => {
  const theme = useTheme();
  const intl = useIntl();

  const denialReasonOptions = [
    "wrong_diploma",
    "wrong_xp",
    "insufficient_languages",
    "closed_position",
    "other",
  ];

  const isOtherSelected = denialReasons.includes("other");

  return (
    <Modal
      open={open}
      onClose={loading ? undefined : onClose}
      sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}
    >
      <Paper
        sx={{
          width: "100%",
          maxWidth: "440px",
          borderRadius: "12px",
          outline: "none",
          overflow: "hidden",
          position: "relative",
          boxShadow: "0px 20px 44px rgba(2, 51, 49, 0.14)",
        }}
      >
        {loading ? (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255, 255, 255, 0.75)",
              borderRadius: "12px",
            }}
          >
            <SquarehubLoader size={72} />
          </Box>
        ) : null}
        <IconButton
          onClick={onClose}
          disabled={loading}
          size="small"
          sx={{ position: "absolute", top: 12, right: 12, color: theme.palette.text.secondary }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <Box sx={{ p: "18px 20px 20px" }}>
          <Typography sx={{ fontWeight: 700, fontSize: "14px", color: theme.palette.text.main }}>
            <FormattedMessage
              id="recruiter.kanban.close_application_question"
              defaultMessage="Are you sure you want to close this application?"
            />
          </Typography>

          <Box
            sx={{
              mt: 1.5,
              p: "10px 12px",
              borderRadius: "10px",
              backgroundColor: theme.palette.secondary.box,
              border: `1px solid ${theme.palette.secondary.grey}`,
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: "13px", color: theme.palette.text.main }}>
              {candidateName || "-"}
            </Typography>
            <Typography sx={{ mt: 0.25, fontSize: "12px", color: theme.palette.text.secondary }}>
              {jobTitle || "-"}
            </Typography>
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography sx={{ mb: 0.5, fontWeight: 700, fontSize: "13px", color: theme.palette.text.main }}>
              <FormattedMessage id="applicant.denial_reason" defaultMessage="Rejection reason(s):" />
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {denialReasonOptions.map((reasonKey) => (
                <FormControlLabel
                  key={reasonKey}
                  control={
                    <Checkbox
                      checked={denialReasons.includes(reasonKey)}
                      onChange={() => onToggleReason?.(reasonKey)}
                      disabled={loading}
                      size="small"
                    />
                  }
                  label={
                    intl.formatMessage({
                      id: `applicant.denial_reasons.${reasonKey}`,
                      defaultMessage: reasonKey,
                    })
                  }
                  sx={{
                    m: 0,
                    "& .MuiFormControlLabel-label": {
                      fontSize: "13px",
                      color: theme.palette.text.main,
                    },
                  }}
                />
              ))}
            </Box>

            {isOtherSelected && (
              <TextField
                fullWidth
                size="small"
                value={otherReason}
                onChange={(event) => onChangeOtherReason?.(event.target.value)}
                disabled={loading}
                placeholder={intl.formatMessage({
                  id: "recruiter.kanban.denial_reason_other_placeholder",
                  defaultMessage: "Please add details",
                })}
                sx={{
                  mt: 1,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                  },
                }}
              />
            )}
          </Box>

          <Box sx={{ mt: 2.5, display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button
              onClick={onClose}
              disabled={loading}
              sx={{
                borderRadius: "999px",
                textTransform: "none",
                color: theme.palette.text.secondary,
                fontWeight: 600,
              }}
            >
              <FormattedMessage id="cancel" defaultMessage="Cancel" />
            </Button>
            <Button
              onClick={onConfirm}
              disabled={loading}
              sx={{
                px: 2.5,
                borderRadius: "999px",
                textTransform: "none",
                color: theme.palette.common.white,
                backgroundColor: theme.palette.error.main,
                fontWeight: 700,
                "&:hover": {
                  backgroundColor: theme.palette.error.dark,
                },
                "&.Mui-disabled": {
                  color: theme.palette.common.white,
                },
              }}
            >
              <FormattedMessage
                id="recruiter.kanban.confirm_close_application"
                defaultMessage="Close application"
              />
            </Button>
          </Box>
        </Box>
      </Paper>
    </Modal>
  );
};

export default CloseJobApplicationModal;
